import React, { useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Flame,
  Wallet,
  Tag,
  Sparkles,
  Plus,
  Lock,
  FolderOpen,
  Box,
  Pencil,
  X,
  Share2,
  SlidersHorizontal,
  AlertCircle,
  ChevronRight,
  ExternalLink,
} from "lucide-react";
import { useEstimate, projectCoins } from "@/context/EstimateContext";
import { usePro } from "@/context/ProContext";
import { FREE_PROJECT_LIMIT, TINGTOWN_URL, REFERRAL } from "@/config/pricing";
import { formatUSD, formatNum, rateLabel } from "@/lib/format";
import { BudgetRing } from "@/components/BudgetRing";
import { ThemeToggle } from "@/components/ThemeToggle";
import { AddProjectDialog } from "@/components/AddProjectDialog";
import { AddPurchaseDialog } from "@/components/AddPurchaseDialog";
import { TownRibbon } from "@/components/TownRibbon";
import { BuildHelpPopup } from "@/components/BuildHelpPopup";
import { toast } from "sonner";

const HERO =
  "https://images.unsplash.com/photo-1710438399422-2fca27686bcd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function Dashboard() {
  const navigate = useNavigate();
  const {
    projects,
    purchases,
    coinsPurchased,
    totalSpent,
    blendedRate,
    totalCoinsUsed,
    totalProjectCost,
    coinsRemaining,
    removePurchase,
    removeProject,
  } = useEstimate();
  const { isPro, trialActive, trialDaysLeft, trialEnded, paid } = usePro();

  const [projectDialog, setProjectDialog] = useState({ open: false, editing: null });
  const [purchaseDialog, setPurchaseDialog] = useState({ open: false, editing: null });

  const overBudget = coinsRemaining < 0;
  const hasPurchases = purchases.length > 0;
  const showBanner = hasPurchases && coinsRemaining <= coinsPurchased * 0.1;
  const atProjectLimit = !isPro && projects.length >= FREE_PROJECT_LIMIT;
  const showTrialChip = trialActive && !paid;
  const showTrialEnded = trialEnded && !isPro;
  const lockedOverLimit = showTrialEnded && projects.length > FREE_PROJECT_LIMIT;

  const openAddProject = () => {
    if (atProjectLimit) {
      navigate("/paywall");
      return;
    }
    setProjectDialog({ open: true, editing: null });
  };

  const handleShare = async () => {
    const lines = [
      "CreditCost — All Projects",
      "",
      `Credits purchased: ${formatNum(coinsPurchased)}`,
      `Total spent: ${formatUSD(totalSpent)}`,
      `Blended rate: ${rateLabel(blendedRate)}`,
      "",
      `Coins used: ${formatNum(totalCoinsUsed)}`,
      `Coins remaining: ${formatNum(coinsRemaining)}`,
      `TOTAL PROJECT COST: ${formatUSD(totalProjectCost)}`,
    ];
    projects.forEach((p) =>
      lines.push(`- ${p.name}: ${formatNum(projectCoins(p))} coins (${formatUSD(projectCoins(p) * blendedRate)})`),
    );
    const text = lines.join("\n");
    try {
      if (navigator.share) await navigator.share({ title: "CreditCost estimate", text });
      else {
        await navigator.clipboard.writeText(text);
        toast.success("Estimate copied to clipboard");
      }
    } catch {
      /* dismissed */
    }
  };

  return (
    <div className="mx-auto max-w-2xl pb-16" data-testid="calculator-dashboard">
      {/* HERO */}
      <div className="hero-scrim rounded-b-2xl px-6 pb-7 pt-6" style={{ "--hero-img": `url(${HERO})` }}>
        <div className="mb-5 flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand">
              <Box size={20} className="text-white" />
            </div>
            <span className="font-display text-lg font-bold text-white">CreditCost</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle light />
            <button
              data-testid="share-button"
              onClick={handleShare}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15 text-white transition-colors hover:bg-white/25"
              aria-label="Share"
            >
              <Share2 size={18} />
            </button>
            <button
              data-testid="settings-button"
              onClick={() => navigate("/settings")}
              className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/15 text-white transition-colors hover:bg-white/25"
              aria-label="Settings"
            >
              <SlidersHorizontal size={18} />
            </button>
          </div>
        </div>

        <p className="mb-1 font-display text-xs font-bold tracking-[0.15em] text-white/75">
          TOTAL PROJECT COST
        </p>
        <div className="flex items-center justify-between gap-4">
          <h1
            className="font-mono text-5xl font-bold text-white tabular truncate"
            data-testid="project-cost"
          >
            {formatUSD(totalProjectCost)}
          </h1>
          {hasPurchases && (
            <BudgetRing progress={coinsPurchased > 0 ? totalCoinsUsed / coinsPurchased : 0} over={overBudget} />
          )}
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          <Chip>
            <Flame size={13} />
            <span data-testid="coins-used">{formatNum(totalCoinsUsed)} used</span>
          </Chip>
          <Chip warn={overBudget} testId="coins-remaining">
            {overBudget ? <AlertCircle size={13} /> : <Wallet size={13} />}
            {overBudget ? `${formatNum(Math.abs(coinsRemaining))} over` : `${formatNum(coinsRemaining)} left`}
          </Chip>
          <Chip>
            <Tag size={13} />
            <span data-testid="blended-rate">{rateLabel(blendedRate)}</span>
          </Chip>
          {showTrialChip && (
            <Chip trial testId="trial-chip">
              <Sparkles size={13} />
              Pro trial: {trialDaysLeft} day{trialDaysLeft === 1 ? "" : "s"} left
            </Chip>
          )}
        </div>
      </div>

      <div className="px-4 pt-5 sm:px-6">
        {showBanner && (
          <button
            data-testid="low-balance-banner"
            onClick={() => setPurchaseDialog({ open: true, editing: null })}
            className={`mb-4 flex w-full items-center gap-2.5 rounded-xl border bg-surface-2 p-3 text-left ${
              overBudget ? "border-danger" : "border-warning"
            }`}
          >
            <AlertCircle size={20} className={overBudget ? "text-danger" : "text-warning"} />
            <span className="text-sm font-medium">
              {overBudget
                ? `You're ${formatNum(Math.abs(coinsRemaining))} coins over your balance — log more credits.`
                : `Only ${formatNum(coinsRemaining)} coins left. Tap to add a purchase.`}
            </span>
          </button>
        )}

        {showTrialEnded && (
          <button
            data-testid="trial-ended-banner"
            onClick={() => navigate("/paywall")}
            className="mb-4 flex w-full items-center gap-2.5 rounded-xl border border-warning bg-surface-2 p-3 text-left"
          >
            <Sparkles size={20} className="text-warning" />
            <span className="text-sm font-medium">
              {lockedOverLimit
                ? `Your Pro trial ended. Editing is locked while you have ${projects.length} projects — upgrade, or delete extras to keep 1 free.`
                : "Your 7-day Pro trial ended. Upgrade for unlimited projects, exports & no promo footer."}
            </span>
          </button>
        )}

        {/* CREDIT PURCHASES */}
        <SectionHead
          label="CREDIT PURCHASES"
          meta={hasPurchases ? `${formatNum(coinsPurchased)} coins · ${formatUSD(totalSpent)}` : null}
          metaTestId="purchase-summary"
        />
        {!hasPurchases ? (
          <Prompt icon={Wallet} testId="purchases-empty">
            Log the credit packs you bought for an exact blended $/coin rate.
          </Prompt>
        ) : (
          purchases.map((p) => (
            <div
              key={p.id}
              role="button"
              onClick={() => setPurchaseDialog({ open: true, editing: p })}
              data-testid={`purchase-${p.id}`}
              className="mb-2 flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-surface-2 px-4 py-3 transition-colors hover:border-border-strong"
            >
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-tint">
                <Wallet size={19} className="text-brand" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{p.label}</p>
                <p className="font-mono text-xs text-muted-foreground tabular">
                  ${(p.dollars / p.coins).toFixed(3)}/coin
                </p>
              </div>
              <span className="font-mono font-bold tabular">{formatUSD(p.dollars)}</span>
              <button
                data-testid={`delete-purchase-${p.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  removePurchase(p.id);
                }}
                className="p-1 text-border-strong hover:text-danger transition-colors"
                aria-label="Delete purchase"
              >
                <X size={18} />
              </button>
            </div>
          ))
        )}
        <OutlineButton testId="add-purchase-button" onClick={() => setPurchaseDialog({ open: true, editing: null })}>
          <Plus size={18} /> Add Purchase
        </OutlineButton>

        {/* PROJECTS */}
        <div className="mt-8">
          <SectionHead
            label="PROJECTS"
            meta={projects.length > 0 ? `${projects.length} project${projects.length > 1 ? "s" : ""}` : null}
            metaTestId="projects-count"
          />
          {projects.length === 0 ? (
            <Prompt icon={FolderOpen} testId="projects-empty">
              Create a project (one per client/build). Each has its own task calculator that rolls up into
              the total above.
            </Prompt>
          ) : (
            projects.map((p) => {
              const coins = projectCoins(p);
              return (
                <div
                  key={p.id}
                  role="button"
                  onClick={() => navigate(`/project/${p.id}`)}
                  data-testid={`project-${p.id}`}
                  className="mb-2 flex cursor-pointer items-center gap-3 rounded-xl border border-border bg-surface-2 px-4 py-3 transition-colors hover:border-border-strong"
                >
                  <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-brand-tint">
                    <Box size={20} className="text-brand" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{p.name}</p>
                    <p className="font-mono text-xs text-muted-foreground tabular">
                      {p.items.length} task{p.items.length === 1 ? "" : "s"} · {formatNum(coins)} coins
                    </p>
                  </div>
                  <span className="font-mono font-bold text-brand tabular">{formatUSD(coins * blendedRate)}</span>
                  <button
                    data-testid={`edit-project-${p.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      setProjectDialog({ open: true, editing: p });
                    }}
                    className="p-1 text-muted-foreground hover:text-foreground transition-colors"
                    aria-label="Rename project"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    data-testid={`delete-project-${p.id}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      removeProject(p.id);
                    }}
                    className="p-1 text-border-strong hover:text-danger transition-colors"
                    aria-label="Delete project"
                  >
                    <X size={18} />
                  </button>
                </div>
              );
            })
          )}
          <button
            data-testid="add-project-button"
            onClick={openAddProject}
            className="mt-1 flex w-full items-center justify-center gap-2 rounded-xl bg-brand py-4 font-display font-bold text-white transition-colors hover:bg-brand-dark"
          >
            {atProjectLimit ? <Lock size={18} /> : <Plus size={20} />}
            {atProjectLimit ? "Unlock Unlimited Projects" : "Add Project"}
          </button>
        </div>

        {/* FOOTER */}
        {paid ? (
          <p className="mt-8 text-center text-sm text-muted-foreground" data-testid="brand-footer">
            Built by T.I.N.G Town · tingtown.com · © 2026 CLUB T.I.N.G LLC
          </p>
        ) : (
          <>
            <div className="mt-8 rounded-xl border border-border bg-surface-2 p-4" data-testid="referral-card">
              <p className="text-sm text-muted-foreground">{REFERRAL.note}</p>
              <a
                data-testid="referral-link"
                href={REFERRAL.url}
                target="_blank"
                rel="noreferrer"
                className="mt-3 flex w-full items-center justify-center gap-2 rounded-lg bg-brand px-4 py-3 font-display text-sm font-bold text-white transition-colors hover:bg-brand-dark"
              >
                Get Free Emergent Credits <ExternalLink size={16} />
              </a>
            </div>
            <a
              data-testid="lead-gen-footer"
              href={TINGTOWN_URL}
              target="_blank"
              rel="noreferrer"
              className="glass mx-auto mt-5 flex w-fit items-center gap-1.5 rounded-full border border-border px-3.5 py-1.5 text-sm text-muted-foreground transition-colors hover:text-foreground"
            >
              <span className="truncate">Built in the T.I.N.G Town Developer District — get your own</span>
              <ChevronRight size={14} />
            </a>
          </>
        )}

        <TownRibbon />
      </div>

      <AddProjectDialog
        open={projectDialog.open}
        editing={projectDialog.editing}
        onOpenChange={(o) => setProjectDialog((s) => ({ ...s, open: o }))}
        onCreated={(id) => navigate(`/project/${id}`)}
      />
      <AddPurchaseDialog
        open={purchaseDialog.open}
        editing={purchaseDialog.editing}
        onOpenChange={(o) => setPurchaseDialog((s) => ({ ...s, open: o }))}
      />
      {!paid && <BuildHelpPopup blocked={projectDialog.open || purchaseDialog.open} />}
    </div>
  );
}

function Chip({ children, warn, trial, testId }) {
  const bg = warn ? "bg-danger/60" : trial ? "bg-brand/70" : "bg-white/18";
  return (
    <span
      data-testid={testId}
      className={`flex items-center gap-1.5 rounded-full px-3 py-1 font-mono text-sm font-semibold text-white tabular ${bg}`}
    >
      {children}
    </span>
  );
}

function SectionHead({ label, meta, metaTestId }) {
  return (
    <div className="mb-3 flex items-center justify-between">
      <p className="font-display text-xs font-bold tracking-wider text-muted-foreground">{label}</p>
      {meta && (
        <p className="font-mono text-xs font-semibold text-brand tabular" data-testid={metaTestId}>
          {meta}
        </p>
      )}
    </div>
  );
}

function Prompt({ icon: Icon, children, testId }) {
  return (
    <div className="mb-2 flex items-center gap-3 rounded-xl bg-brand-tint p-4" data-testid={testId}>
      <Icon size={22} className="shrink-0 text-brand" />
      <p className="text-sm leading-snug text-foreground/80">{children}</p>
    </div>
  );
}

function OutlineButton({ children, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className="flex w-full items-center justify-center gap-1.5 rounded-xl border-[1.5px] border-brand py-3 font-display font-bold text-brand transition-colors hover:bg-brand-tint"
    >
      {children}
    </button>
  );
}
